import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HttpClient } from '@angular/common/http';
import { debounceTime, map, of, startWith } from 'rxjs';

import { SelfServiceStateService } from '../../shared/self-service-state.service';
import { DatasetColumn } from '../../shared/self-service-types';
import { GlobalFilterService } from '../../../shared-services/global-filter-service';
import { OnTheFlyComputationService, OtfComputedResult, OtfExpression } from './on-the-fly-computation.service';
import { environment } from '../../../../../../my-app/src/environments/environment';

export interface ComputationEntry {
  id: string;
  expression: string;
  alias: string;
  raw: string;
}

export interface OtfAppliedConfig {
  widgetId: string;
  entityName: string;
  sliderValue: number;
  expressions: OtfExpression[];
}

interface GetDataPayload {
  entityName: string;
  attributesList: string[];
  filterList: unknown[];
}

@Component({
  selector: 'app-on-the-fly-computation',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule],
  templateUrl: './on-the-fly-computation.component.html',
  styleUrl: './on-the-fly-computation.component.scss',
})
export class OnTheFlyComputationComponent implements OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Emits raw API response when entity-backed computation completes. */
  @Output() dataResult = new EventEmitter<Record<string, unknown> | Record<string, unknown>[]>();

  /** Emits the saved computation definition so the parent widget can rerun it. */
  @Output() computationApplied = new EventEmitter<OtfAppliedConfig>();

  /** Emits all rows with computed columns appended after client-side Apply. */
  @Output() appliedRows = new EventEmitter<Record<string, unknown>[]>();

  @Input() compact = false;
  @Input() widgetId = '';

  @Input() set overrideColumns(v: DatasetColumn[] | null) { this._overrideCols.set(v); }
  @Input() set overrideEntityName(v: string | null) { this._overrideEntity.set(v); }

  /**
   * Rows from the parent chart/widget used for client-side expression evaluation
   * and live preview. When provided, Apply produces client-side computed rows
   * in addition to (or instead of) a backend call.
   */
  @Input() set baseRows(v: Record<string, unknown>[]) { this._baseRows.set(v ?? []); }

  private readonly _overrideCols = signal<DatasetColumn[] | null>(null);
  private readonly _overrideEntity = signal<string | null>(null);
  private readonly _baseRows = signal<Record<string, unknown>[]>([]);

  private readonly http = inject(HttpClient);
  private readonly state = inject(SelfServiceStateService);
  private readonly globalFilterSvc = inject(GlobalFilterService, { optional: true });
  private readonly computationSvc = inject(OnTheFlyComputationService);

  readonly allColumns = computed<DatasetColumn[]>(() =>
    this._overrideCols() ?? this.state.dataset()?.columns ?? [],
  );
  readonly entityName = computed(() =>
    this._overrideEntity() ?? this.state.dataset()?.fileName ?? '',
  );

  computations = signal<ComputationEntry[]>([]);
  sliderValue = signal<number>(1);
  sliderMin = 0;
  sliderMax = 100;

  currentExpression = '';
  currentAlias = '';
  expressionError = '';

  loading = signal(false);
  errorMessage = signal('');
  successMessage = signal('');

  private buildFilterChips(): { key: string; value: unknown }[] {
    const params = this.globalFilterSvc?.getCurrentFilters()?.requestParams ?? {};
    return Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([key, value]) => ({ key, value }));
  }

  readonly activeDashboardFilters = toSignal(
    (this.globalFilterSvc?.filter$ ?? of(null)).pipe(
      debounceTime(150),
      map(() => this.buildFilterChips()),
      startWith(this.buildFilterChips()),
    ),
    { initialValue: [] as { key: string; value: unknown }[] },
  );

  /**
   * Live-computed label:value results shown below the slider.
   * Re-evaluates any time expressions, sliderValue, or baseRows change.
   * Uses the first row from baseRows as the sample row; falls back to a
   * sliderValue-only context when no rows are available.
   */
  readonly liveComputedResults = computed<OtfComputedResult[]>(() => {
    const comps = this.computations();
    if (!comps.length) return [];

    const sv = this.sliderValue();
    const rows = this._baseRows();
    const sampleRow: Record<string, unknown> = rows.length > 0
      ? { ...rows[0], sliderValue: sv }
      : { sliderValue: sv };

    const expressions: OtfExpression[] = comps.map((c) => ({
      expression: c.expression,
      alias: c.alias,
      raw: c.raw,
    }));
    return this.computationSvc.evaluatePreviewRow(expressions, sampleRow, sv);
  });

  readonly hasLiveResults = computed(() => this.liveComputedResults().length > 0);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.errorMessage.set('');
      this.successMessage.set('');
    }
  }

  appendSliderValue(): void {
    this.currentExpression = this.currentExpression
      ? `${this.currentExpression} + sliderValue`
      : 'sliderValue';
    this.expressionError = '';
  }

  updateSliderFromRange(event: Event): void {
    this.sliderValue.set(parseFloat((event.target as HTMLInputElement).value));
  }

  updateSliderFromNumber(event: Event): void {
    const val = parseFloat((event.target as HTMLInputElement).value);
    if (!isNaN(val)) this.sliderValue.set(val);
  }

  appendColumn(col: DatasetColumn): void {
    const token = col.name.includes(' ') ? `[${col.name}]` : col.name;
    this.currentExpression = this.currentExpression
      ? `${this.currentExpression} + ${token}`
      : token;
    if (!this.currentAlias) this.currentAlias = col.name.replace(/\s+/g, '_');
    this.expressionError = '';
  }

  addComputation(): void {
    const expr = this.currentExpression.trim();
    const alias = this.currentAlias.trim();
    if (!expr) { this.expressionError = 'Expression is required.'; return; }
    if (!alias) { this.expressionError = 'Alias is required.'; return; }
    const duplicate = this.computations().some((c) => c.alias === alias);
    if (duplicate) { this.expressionError = `Alias "${alias}" already exists. Use a different name.`; return; }
    this.expressionError = '';
    const raw = `${expr} as ${alias}`;
    this.computations.update((list) => [
      ...list,
      { id: `comp-${Date.now()}`, expression: expr, alias, raw },
    ]);
    this.currentExpression = '';
    this.currentAlias = '';
  }

  removeComputation(id: string): void {
    this.computations.update((list) => list.filter((c) => c.id !== id));
  }

  clearAll(): void {
    this.computations.set([]);
    this.sliderValue.set(1);
    this.currentExpression = '';
    this.currentAlias = '';
    this.expressionError = '';
    this.errorMessage.set('');
    this.successMessage.set('');
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  apply(): void {
    if (!this.computations().length) {
      this.errorMessage.set('Add at least one computation before applying.');
      return;
    }

    this.errorMessage.set('');
    this.successMessage.set('');

    const sv = this.sliderValue();
    const expressionsForSave: OtfExpression[] = this.computations().map((c) => ({
      expression: c.expression,
      alias: c.alias,
      raw: c.raw,
    }));
    if (this.widgetId) {
      this.computationSvc.setExpressions(this.widgetId, expressionsForSave);
    }
    this.computationApplied.emit({
      widgetId: this.widgetId,
      entityName: this.entityName(),
      sliderValue: sv,
      expressions: expressionsForSave,
    });

    const resolveTokens = (expr: string) => expr.replace(/\bsliderValue\b/g, String(sv));

    // ── Client-side path: rows available → apply expressions and emit ──────────
    const rows = this._baseRows();
    if (rows.length > 0) {
      const expressions: OtfExpression[] = this.computations().map((c) => ({
        expression: resolveTokens(c.expression),
        alias: c.alias,
        raw: resolveTokens(c.raw),
      }));
      const computed = this.computationSvc.applyExpressionsToRows(rows, expressions, sv);
      this.appliedRows.emit(computed);
      this.successMessage.set(`Computed ${expressions.length} expression(s) across ${computed.length} rows.`);
    }

    // ── Entity-backed path: entity name available → call /getData backend ──────
    const entity = this.entityName();
    if (!entity) {
      if (!rows.length) {
        this.errorMessage.set('No dataset loaded.');
        return;
      }
      this.close();
      return;
    }

    this.loading.set(true);

    const chips = this.buildFilterChips();
    const filterList = chips.map(({ key, value }, i) => ({
      operator: '=',
      attributeName: key,
      attributeValue: String(Array.isArray(value) ? value[0] : value),
      logicalOperator: i < chips.length - 1 ? 'AND' : '',
    }));

    const payload: GetDataPayload = {
      entityName: entity,
      attributesList: this.computations().map((c) => resolveTokens(c.raw)),
      filterList,
    };

    this.http
      .post<Record<string, unknown>>(
        `${String(environment.URL || '').replace(/\/$/, '')}/getData`,
        payload,
      )
      .subscribe({
        next: (result) => {
          this.loading.set(false);
          this.successMessage.set('Data fetched successfully.');
          this.dataResult.emit(result);
          this.close();
        },
        error: (err: { error?: { message?: string }; message?: string }) => {
          this.loading.set(false);
          this.errorMessage.set(err?.error?.message ?? err?.message ?? 'Failed to fetch data.');
        },
      });
  }

  trackById(_i: number, item: ComputationEntry): string {
    return item.id;
  }

  formatResultValue(value: number | string | null): string {
    if (value === null) return '?';
    if (typeof value === 'number') {
      return Number.isInteger(value) ? String(value) : value.toFixed(4).replace(/\.?0+$/, '');
    }
    return String(value);
  }
}
